import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Coffee, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";

const fallbackSpaces = [];

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/dec1epwv_ChatGPT%20Image%2015%20mei%202026%2C%2012_49_13.png";
const TEXTURE_BG =
  "https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/mfh1dapq_Gevelnoordstraat.jpeg";

const useReveal = (deps = []) => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal:not(.in)");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export default function Home() {
  const [spaces, setSpaces] = useState(fallbackSpaces);
  useReveal([spaces]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/spaces");
        setSpaces(res.data.spaces || []);
      } catch (err) {
        console.error("Failed to load spaces:", err);
      }
    })();
  }, []);

  return (
    <main data-testid="home-page">
      <Seo
        title="Noordstraat 140 — Coworking, kantoorruimtes & eventlocatie in Roeselare"
        description="Coworking, flexplekken, kantoorruimtes op maat, vergaderzalen, een fotostudio en eventlocatie in het hart van Roeselare. Een gerestaureerde oude koffiebranderij voor freelancers, ondernemers en creatieven."
        canonical="https://www.noordstraat140.com/"
      />
      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-12 md:pt-16 pb-20 md:pb-28">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <p className="overline" style={{ color: "var(--accent)" }}>
                Roeselare · Oude koffiebranderij
              </p>
              <h1
                className="font-serif font-light tracking-tighter leading-[0.92] mt-6"
                style={{ fontSize: "clamp(48px, 8.6vw, 132px)" }}
                data-testid="hero-title"
              >
                Let's
                <br />
                <em className="not-italic" style={{ color: "var(--accent)" }}>
                  get
                </em>
                <br />
                together.
              </h1>
            </div>

            <div className="col-span-12 md:col-span-5 md:pb-6">
              <p
                className="text-base md:text-lg leading-relaxed max-w-md"
                style={{ color: "var(--text-secondary)" }}
              >
                Noordstraat 140 is een gerestaureerd herenhuis en oude
                koffiebranderij. In authentieke ruimtes vind je een
                innovatieve co-working, kantoorunits op maat en een
                inspirerende meeting- & eventlocatie, voor starters,
                groeiende ondernemers, teams en hun klanten.
              </p>

              <div className="flex flex-wrap gap-3 mt-8">
                <Link to="/book-desk" className="btn-primary" data-testid="hero-book-desk-btn">
                  Boek een bureau <ArrowUpRight size={16} />
                </Link>
                <a href="#spaces" className="btn-ghost" data-testid="hero-see-spaces-btn">
                  Bekijk de ruimtes
                </a>
              </div>
            </div>
          </div>

          {/* Hero image */}
          <div className="mt-14 md:mt-20 reveal">
            <div className="relative aspect-[16/9] overflow-hidden">
              <img
                src={HERO_IMG}
                alt="Noordstraat 140 — gevel in het zacht avondlicht"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 78%" }}
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section
        className="relative grain"
        style={{ backgroundColor: "var(--bg-surface)" }}
        data-testid="intro-strip"
        id="about"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32 relative">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <p className="overline" style={{ color: "var(--accent)" }}>Het Pand</p>
              <div className="divider mt-5" />
            </div>
            <div className="col-span-12 md:col-span-8">
              <p className="font-serif text-3xl md:text-5xl leading-[1.08] font-light tracking-tight reveal">
                Een huis voor ondernemers, creatievelingen en freelancers.
                Een plek om samen te werken, kennis uit te wisselen en
                elkaar te inspireren.
              </p>

              <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
                {[
                  { icon: <Coffee size={18} />, title: "Oude koffiebranderij", body: "Authentieke industriële architectuur, gerestaureerd en heringericht voor werk, ontmoeting en creatie." },
                  { icon: <Sparkles size={18} />, title: "Flexibele oplossingen", body: "Van een dag aan een single desk tot kantoorunits op maat voor ondernemers in groeifase." },
                  { icon: <MapPin size={18} />, title: "Centrum Roeselare", body: "Vlot bereikbaar, met private parking op de binnenkoer voor je gasten of crew." },
                ].map((c, i) => (
                  <div key={c.title} className="reveal" style={{ transitionDelay: `${i * 120}ms` }}>
                    <div style={{ color: "var(--accent)" }} className="mb-4">{c.icon}</div>
                    <p className="font-serif text-xl mb-2">{c.title}</p>
                    <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                      {c.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SFEER-PAUZE: overgang naar de ruimtes */}
      <section className="relative" data-testid="atmosphere-keuken">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 reveal">
          <div
            className="relative aspect-[16/8] overflow-hidden"
            style={{ backgroundColor: "#1c1814" }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/hzfeay19_IMG_2255.jpeg"
              alt="Glas in lood op de eikenhouten tafel, Noordstraat 140"
              className="w-full h-full object-cover"
              style={{
                filter: "contrast(1.12) saturate(1.04) brightness(0.94)",
              }}
            />
            {/* Vignette / editorial overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0) 35%, rgba(0,0,0,0.45) 100%)",
              }}
            />
            {/* Subtle dark gradient bottom */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to top, rgba(28,24,20,0.55) 0%, rgba(28,24,20,0) 35%)",
              }}
            />
          </div>
        </div>
      </section>

      {/* SPACES */}
      <section id="spaces" className="relative" data-testid="spaces-section">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-6 md:gap-8 mb-16">
            <div className="col-span-12 md:col-span-4">
              <p className="overline" style={{ color: "var(--accent)" }}>Ruimtes</p>
              <div className="divider mt-5" />
            </div>
            <div className="col-span-12 md:col-span-8">
              <h2 className="font-serif text-4xl md:text-6xl font-light leading-[1.02] tracking-tight">
                Kies je ruimte.
              </h2>
              <p className="mt-5 max-w-xl text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
                Vergaderingen, workshops, foto- en filmshoots, events of
                gewoon een dag geconcentreerd werken. Voor elke ondernemer
                is er een ruimte op maat.
              </p>
            </div>
          </div>

          {/* Asymmetric grid */}
          <div className="grid grid-cols-12 gap-6 md:gap-8" data-testid="spaces-grid">
            {spaces.map((s, i) => {
              const span =
                i % 5 === 0
                  ? "col-span-12 md:col-span-8"
                  : i % 5 === 1
                  ? "col-span-12 md:col-span-4"
                  : i % 5 === 2
                  ? "col-span-12 md:col-span-5"
                  : i % 5 === 3
                  ? "col-span-12 md:col-span-7"
                  : "col-span-12";
              return (
                <Link
                  to={s.slug === "single-desk" ? "/book-desk" : `/spaces/${s.slug}`}
                  key={s.slug}
                  data-testid={`space-card-${s.slug}`}
                  className={`${span} group block reveal`}
                  style={{ transitionDelay: `${i * 90}ms` }}
                >
                  <div className={`relative ${i % 5 === 4 ? "aspect-[21/9]" : "aspect-[5/4]"} overflow-hidden`}>
                    <img
                      src={s.image}
                      alt={s.name}
                      className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    />
                    <div className="absolute top-4 left-4">
                      <span
                        className="overline px-3 py-1.5"
                        style={{ backgroundColor: "var(--bg-base)", color: "var(--text-primary)" }}
                      >
                        {s.booking_type === "payment" ? "Online boeken" : "Aanvraag"}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:justify-between mt-5 gap-3 sm:gap-6">
                    <div className="min-w-0">
                      <p className="font-serif text-2xl md:text-3xl font-light leading-tight">
                        {s.name}
                      </p>
                      <p className="text-sm mt-1 italic" style={{ color: "var(--text-secondary)" }}>
                        {s.tagline}
                      </p>
                    </div>
                    <div className="text-left sm:text-right sm:shrink-0">
                      <p className="overline" style={{ color: "var(--text-secondary)" }}>
                        Vanaf
                      </p>
                      <p className="font-serif text-base sm:text-lg mt-1">{s.price_from}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* SFEER-PAUZE: binnenkoer — voor de CTA-band */}
      <section className="relative" data-testid="atmosphere-binnenkoer">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-6 md:py-10 reveal">
          <div className="aspect-[16/8] overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/8apxe9gs_noordstraatbinnenkoer.jpg"
              alt="De binnenkoer met de oude branderij-schouw"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA texture band */}
      <section
        className="relative grain overflow-hidden"
        style={{
          backgroundImage: `url(https://customer-assets.emergentagent.com/job_venue-central-2/artifacts/4o8l2oqj_terras.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="cta-band"
        id="visit"
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(46, 38, 32, 0.55)" }}
        />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-32 md:py-44 relative"
             style={{ color: "var(--footer-text)" }}>
          <div className="grid grid-cols-12 gap-6 md:gap-8">
            <div className="col-span-12 md:col-span-7">
              <p className="overline" style={{ color: "var(--accent-soft)" }}>
                Kom het bekijken
              </p>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight mt-6">
                Bezoeken op afspraak,
                <br /> van dinsdag tot vrijdag.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:pl-10 md:border-l flex flex-col justify-end gap-6"
                 style={{ borderColor: "rgba(253,251,247,0.18)" }}>
              <p className="text-base leading-relaxed opacity-90">
                Kom je graag eens kijken? Maak een afspraak voor een rondleiding.
              </p>
              <a
                href="mailto:liesbet@clementine.tv"
                className="btn-primary self-start"
                data-testid="cta-tour-btn"
                style={{ backgroundColor: "var(--bg-base)", color: "var(--text-primary)" }}
              >
                Vraag een rondleiding aan <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE / TESTIMONIAL */}
      <section className="relative" data-testid="quote-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-28 md:py-40 text-center">
          <p className="overline" style={{ color: "var(--accent)" }}>
            Voor wie hier werkt
          </p>
          <blockquote className="font-serif text-3xl md:text-5xl font-light leading-[1.15] tracking-tight mt-8 reveal">
            "Wat Noordstraat 140 vooral onderscheidt van alle andere
            kantoorpanden, is dat er een warme, gezellige vibe heerst,
            geen corporate kantoren. Een verrassende plek in het centrum
            van de stad. We krijgen altijd complimenten van onze
            bezoekers, en onder de huurders is er ook een goed contact."
          </blockquote>
          <p className="overline mt-10" style={{ color: "var(--text-secondary)" }}>
            Barbara · Clementine.tv
          </p>
        </div>
      </section>
    </main>
  );
}
